import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useAssignAssociate } from './useAssignAssociate';

/**
 * Destination picker for click-to-assign: a destination dropdown, an aisle span for cluster
 * destinations, and the Assign button. Shared by the board's EmployeeIcon popover and the
 * Scan-To-Assign card so both offer the same destinations and enforce the same rules.
 *
 * @param {Object}   props.associate   the associate being placed
 * @param {Object}   props.from        MOVE_ASSOCIATE `from` for the calling surface
 * @param {Function} props.onAssigned  called after a successful assign (close popover/modal)
 */
export const AssignDestinationPicker = ({ associate, from, onAssigned }) => {
  const {
    assignDestinations,
    selectedDestination,
    selectedDestinationMeta,
    aisleOptions,
    minAisle,
    maxAisle,
    setMinAisle,
    setMaxAisle,
    onChangeDestination,
    isAssignDisabled,
    onClickAssign
  } = useAssignAssociate({ associate, from, onAssigned });

  if (assignDestinations.length === 0) {
    return (
      <Row alignmentHorizontal="center">
        <Text type="b200" color="secondary">
          No eligible locations available
        </Text>
      </Row>
    );
  }

  return (
    <Column spacing="200">
      <Row spacing="200" alignmentVertical="bottom">
        <Select
          value={selectedDestination}
          onChange={onChangeDestination}
          placeholder="Assign to…"
          size="small"
          width={220}>
          {assignDestinations.map((dest) => (
            <SelectOption key={dest.value} value={dest.value} label={dest.label} />
          ))}
        </Select>
        <Button onClick={onClickAssign} type="primary" size="small" disabled={isAssignDisabled}>
          Assign
        </Button>
      </Row>
      {selectedDestinationMeta && selectedDestinationMeta.isAisle && (
        <Row spacing="200" alignmentVertical="center">
          <Text type="b200" color="secondary">
            Aisles:
          </Text>
          {aisleOptions.length > 0 ? (
            <React.Fragment>
              <Select value={minAisle} onChange={setMinAisle} placeholder="From" size="small" width={90}>
                {aisleOptions.map((aisleNo) => (
                  <SelectOption key={aisleNo} value={aisleNo} label={`${aisleNo}`} />
                ))}
              </Select>
              <Text type="b200" color="secondary">
                to
              </Text>
              <Select value={maxAisle} onChange={setMaxAisle} placeholder="To" size="small" width={90}>
                {aisleOptions.map((aisleNo) => (
                  <SelectOption key={aisleNo} value={aisleNo} label={`${aisleNo}`} />
                ))}
              </Select>
            </React.Fragment>
          ) : (
            <Text type="b200" color="secondary">
              No aisles available
            </Text>
          )}
        </Row>
      )}
    </Column>
  );
};

export default AssignDestinationPicker;
